'use client';

import { type ReactNode, useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import type { PreviewCoupon } from '../../functions/applyCouponsToUnitPrice';
import { fetchCouponPreview } from '../../functions/fetchCouponPreview';
import { CouponPreviewContext } from './context';

/**
 * Resolves the display-only coupon preview for the PDP from a `?coupon=XXX` URL
 * param and shares it (price cell + Buy CTA read it via
 * `useCouponPreviewCoupon`). Mirrors PopshopRN's `useCouponPreview`: a public,
 * non-redeeming fetch — the real redeem happens later in checkout (the code is
 * also forwarded onto the checkout URL by `BuyNowAction`).
 *
 * No `?coupon=` → renders children with a null preview (zero cost).
 */
export function CouponPreviewProvider({
  storeId,
  listingId,
  children,
}: {
  storeId: string;
  listingId: string;
  children: ReactNode;
}) {
  const code = useSearchParams().get('coupon') ?? undefined;
  const [coupon, setCoupon] = useState<PreviewCoupon | null>(null);

  useEffect(() => {
    if (!code) {
      setCoupon(null);
      return;
    }
    let cancelled = false;
    void fetchCouponPreview({ sellerId: storeId, listingId, code }).then(
      preview => {
        if (!cancelled) setCoupon(preview);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [code, storeId, listingId]);

  return (
    <CouponPreviewContext.Provider value={{ coupon }}>
      {children}
    </CouponPreviewContext.Provider>
  );
}
